import type { RuleSet, Tier } from "../../api/contracts";
import { formatMinutes } from "./format";
import { formatDuration } from "../../lib/duration";
import { matchTier } from "../../btb/rules";
import { RULE_STEPS, hoursText } from "../../btb/constants";
import { summarizeProtectedHours, diffProtectedHours } from "./protected-hours";

export interface CoverageSegment { from: number; to: number | null; kind: "none" | "tier" | "gap"; tierIndex: number | null; blockMinutes: number | null }

/** Segments of the "open time" axis for the coverage bar. Overlapping tiers are a validation error and are drawn as given. */
export function tierCoverage(tiers: Tier[]): CoverageSegment[] {
  const sorted = tiers.map((t, i) => ({ ...t, i })).sort((a, b) => a.minGap - b.minGap);
  const out: CoverageSegment[] = [];
  let cursor = 0;
  for (const t of sorted) {
    if (t.minGap > cursor) out.push({ from: cursor, to: t.minGap - 1, kind: out.length === 0 ? "none" : "gap", tierIndex: null, blockMinutes: null });
    out.push({ from: t.minGap, to: t.maxGap, kind: "tier", tierIndex: t.i, blockMinutes: t.blockMinutes });
    if (t.maxGap === null) return out;
    cursor = t.maxGap + 1;
  }
  out.push({ from: cursor, to: null, kind: out.length === 0 ? "none" : "gap", tierIndex: null, blockMinutes: null });
  return out;
}

const openText = (t: Tier) => (t.maxGap === null ? `${formatDuration(t.minGap)} or more open` : `${formatDuration(t.minGap)} to just under ${formatDuration(t.maxGap + 1)} open`);
/** Leads with the block, which is what owners think in: "30 min BTB block for 1.5 hours to just under 2 hours open". */
export const tierText = (t: Tier) => `${formatDuration(t.blockMinutes, "length")} BTB block for ${openText(t)}`;
const sameTier = (a: Tier, b: Tier) => a.minGap === b.minGap && a.maxGap === b.maxGap && a.blockMinutes === b.blockMinutes;

/** Plain-language summary of a rule set, e.g. for the opt-in dialog. */
export function describeRuleSet(r: RuleSet): string[] {
  const tiers = [...r.tiers].sort((x, y) => x.minGap - y.minGap);
  return [
    ...(tiers[0] && tiers[0].minGap > 0 ? [`No BTB block under ${formatDuration(tiers[0].minGap)} open`] : []),
    ...tiers.map(tierText),
    `Shifts shorter than ${formatMinutes(r.minShiftMinutes)} get no BTB block`,
    `Once a day is ${r.removalUtilizationPct}% booked, no new BTB blocks are added, and existing ones come off when less than ${formatMinutes(r.removalMinGapMinutes)} is open next to them`,
    r.turnoverMaxMinutes > 0
      ? `Cleaning or setup blocks up to ${formatMinutes(r.turnoverMaxMinutes)} at the start or end of a shift count as its edge`
      : "Cleaning or setup blocks get no special treatment",
    `BTB blocks are planned ${lookaheadLabel(r.lookaheadDays)} ahead`,
    r.startLockHoursBefore === null ? "Start times are not locked" : `Start times lock ${hoursText(r.startLockHoursBefore)} before each shift starts`,
    r.endLockHoursBefore === null ? "End times are not locked" : `End times lock ${hoursText(r.endLockHoursBefore)} before each shift ends`,
    `Protected hours: ${summarizeProtectedHours(r.protectedHours)}`,
    r.aiBookingEnabled ? "The AI booking assistant can book a client into BTB time when they ask for that exact time" : "The AI booking assistant books only open time",
    r.adoptManualBtb ? "Hand-placed BTB blocks at the start or end of a shift are managed like ours" : "Hand-placed BTB blocks are left alone",
    r.keepSomeoneIn ? "Someone on shift always stays open; who stays open takes turns through the week" : "Everyone on shift can be blocked at the same time",
  ];
}

/**
 * Plain-language lines for the confirm dialog. Tiers are matched by where they start, which the
 * editor never moves on an add or remove, so a removed rule is named as removed.
 */
export function diffRuleSets(before: RuleSet, after: RuleSet): string[] {
  const starts = [...new Set([...before.tiers, ...after.tiers].map((t) => t.minGap))].sort((x, y) => x - y);
  const lines: string[] = [];
  for (const start of starts) {
    const a = before.tiers.find((t) => t.minGap === start), b = after.tiers.find((t) => t.minGap === start);
    if (a && b && !sameTier(a, b)) lines.push(`${tierText(a)} becomes ${tierText(b)}`);
    else if (a && !b) lines.push(`Removed: ${tierText(a)}`);
    else if (!a && b) lines.push(`Added: ${tierText(b)}`);
  }
  if (before.minShiftMinutes !== after.minShiftMinutes) lines.push(`Minimum shift length for BTB blocks: ${formatMinutes(before.minShiftMinutes)} → ${formatMinutes(after.minShiftMinutes)}`);
  if (before.removalUtilizationPct !== after.removalUtilizationPct) lines.push(`Remove BTB blocks when the day is at least ${before.removalUtilizationPct}% → ${after.removalUtilizationPct}% booked`);
  if (before.removalMinGapMinutes !== after.removalMinGapMinutes) lines.push(`…and less than ${formatMinutes(before.removalMinGapMinutes)} → ${formatMinutes(after.removalMinGapMinutes)} open next to them`);
  const turnover = (n: number) => (n === 0 ? "off" : `up to ${formatMinutes(n)}`);
  if (before.turnoverMaxMinutes !== after.turnoverMaxMinutes) lines.push(`Cleaning / setup blocks counted as the shift edge: ${turnover(before.turnoverMaxMinutes)} → ${turnover(after.turnoverMaxMinutes)}`);
  if (before.lookaheadDays !== after.lookaheadDays) lines.push(`Plan BTB blocks ${lookaheadLabel(before.lookaheadDays)} → ${lookaheadLabel(after.lookaheadDays)} ahead`);
  const lock = (h: number | null) => (h === null ? "off" : `${hoursText(h)} before`);
  if (before.startLockHoursBefore !== after.startLockHoursBefore) lines.push(`Lock start times (before the shift starts): ${lock(before.startLockHoursBefore)} → ${lock(after.startLockHoursBefore)}`);
  if (before.endLockHoursBefore !== after.endLockHoursBefore) lines.push(`Lock end times (before the shift ends): ${lock(before.endLockHoursBefore)} → ${lock(after.endLockHoursBefore)}`);
  lines.push(...diffProtectedHours(before.protectedHours, after.protectedHours));
  const onOff = (b: boolean) => (b ? "on" : "off");
  if (before.aiBookingEnabled !== after.aiBookingEnabled) lines.push(`AI booking into BTB time: ${onOff(before.aiBookingEnabled)} → ${onOff(after.aiBookingEnabled)}`);
  if (before.adoptManualBtb !== after.adoptManualBtb) lines.push(`Managers' BTB blocks: ${onOff(before.adoptManualBtb)} → ${onOff(after.adoptManualBtb)}`);
  if (before.keepSomeoneIn !== after.keepSomeoneIn) lines.push(`Keep someone in the salon: ${onOff(before.keepSomeoneIn)} → ${onOff(after.keepSomeoneIn)}`);
  return lines;
}

/** Explainer / guide sentence for "Keep someone in the salon"; empty when the switch is off (nothing is rendered). */
export function coverageSentence(on: boolean): string {
  return on ? "Someone on shift always stays open for walk-ins, and who stays open takes turns through the week." : "";
}

/**
 * Rule sets apply whole, so a location (or, at the corporate default, an owner group) with its own rules keeps its own
 * AI booking setting. When the switch changes at a wider scope, name the locations it does not reach; null = none.
 */
export function aiBookingHoldoutSentence(before: boolean, after: boolean, choice: ScopeChoice,
  locations: HoldoutLocation[]): string | null {
  return ruleHoldoutSentence("AI booking", before, after, choice, locations);
}

type HoldoutLocation = { name: string; ownerIdentifier: string; ruleScope: "global" | "owner" | "location" };

/** The same holdout note for any on/off rule; `what` names the setting as the sentence says it. */
export function ruleHoldoutSentence(what: string, before: boolean, after: boolean, choice: ScopeChoice, locations: HoldoutLocation[]): string | null {
  if (before === after || choice.scope === "location") return null;
  const names = locations
    .filter((l) => (choice.scope === "global" ? l.ruleScope !== "global" : l.ownerIdentifier === choice.key && l.ruleScope === "location"))
    .map((l) => l.name);
  if (names.length === 0) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  return names.length === 1
    ? `${list} has its own rules, so this does not change ${what} there. Choose it under "One location" to change it.`
    : `${list} have their own rules, so this does not change ${what} there. Choose each under "One location" to change it.`;
}

/** Banner text after a rules save or clear; null = nothing to say. */
export function replanNotice(replan: { locations: number; jobs: number } | null | undefined): string | null {
  if (replan === undefined) return null;
  if (replan === null) return "Saved. The re-plan could not be started now; tonight's re-plan will apply these rules.";
  if (replan.locations === 0) return null;
  return `Re-planning BTB blocks for ${replan.locations} location${replan.locations === 1 ? "" : "s"}.`;
}

export type ScopeChoice = { scope: "global"; key: null } | { scope: "owner" | "location"; key: string };
export function encodeScope(c: ScopeChoice): string { return c.scope === "global" ? "global" : `${c.scope}:${c.key}`; }
export function decodeScope(v: string): ScopeChoice {
  if (v === "global") return { scope: "global", key: null };
  const i = v.indexOf(":");
  const scope = v.slice(0, i);
  if (scope !== "owner" && scope !== "location") throw new Error(`bad scope ${v}`);
  return { scope, key: v.slice(i + 1) };
}

/** "9:00" plus `minutesFromNine`, 12-hour, no am/pm: the explainer's example day is illustrative. */
export function clock(minutesFromNine: number): string {
  const total = 9 * 60 + minutesFromNine;
  const h = Math.floor(total / 60) % 24, m = total % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")}`;
}

export interface WorkedExample {
  gap: number; tierIndex: number; tierFrom: number; tierTo: number | null; blockMinutes: number;
  firstAppointment: string; blockEnd: string;
  top: { from: number; to: number | null; blockMinutes: number } | null;
}

/**
 * An example gap 15 minutes into the lowest tier (clamped to that tier), matched with the
 * planner's own `matchTier` so the story and the planner cannot disagree. Null when there are no
 * tiers or the gap matches none (an invalid draft).
 */
export function workedExample(r: RuleSet): WorkedExample | null {
  const tiers = [...r.tiers].sort((a, b) => a.minGap - b.minGap);
  const first = tiers[0];
  if (!first) return null;
  const gap = first.maxGap === null ? first.minGap + 15 : Math.min(first.minGap + 15, first.maxGap);
  const i = matchTier(gap, tiers);
  if (i === null) return null;
  const t = tiers[i];
  const top = tiers.length > 1 ? tiers[tiers.length - 1] : null;
  return {
    gap, tierIndex: i, tierFrom: t.minGap, tierTo: t.maxGap, blockMinutes: t.blockMinutes,
    firstAppointment: clock(gap), blockEnd: clock(t.blockMinutes),
    top: top ? { from: top.minGap, to: top.maxGap, blockMinutes: top.blockMinutes } : null,
  };
}

const rangeWords = (from: number, to: number | null) => (to === null ? `${formatDuration(from)} or more` : `${formatDuration(from)} to just under ${formatDuration(to + 1)}`);

export function exampleSentences(e: WorkedExample, r: RuleSet): string[] {
  return [
    `A shift starts at 9:00 and the first appointment is at ${e.firstAppointment}, so ${formatDuration(e.gap)} is open at the start. That is at least ${formatDuration(e.tierFrom)}, so a ${formatDuration(e.blockMinutes, "length")} BTB block goes at 9:00 to ${e.blockEnd}. The end of a shift works the same way.`,
    `Once this day is ${r.removalUtilizationPct}% booked and less than ${formatDuration(r.removalMinGapMinutes)} is open next to the block, the block comes off. New blocks are not added to a day that is already ${r.removalUtilizationPct}% booked.`,
    ...(e.top ? [`With ${rangeWords(e.top.from, e.top.to)} open, the block is ${formatDuration(e.top.blockMinutes)}.`] : []),
  ];
}

/**
 * When a shift edge at `edgeHour` (local, 0–23) locks `hoursBefore` hours ahead, for the worked
 * examples: "9pm the evening before", "10am that day".
 */
export function lockExample(edgeHour: number, hoursBefore: number): string {
  const at = edgeHour - hoursBefore;
  const h = ((at % 24) + 24) % 24;
  const time = `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "am" : "pm"}`;
  const daysBack = Math.ceil(-at / 24);
  return `${time} ${daysBack <= 0 ? "that day" : daysBack === 1 ? "the evening before" : `${daysBack} days before`}`;
}

/** The schedule locks in owner words, from the rule set so the copy follows what is saved. */
export function lockSentence(r: Pick<RuleSet, "startLockHoursBefore" | "endLockHoursBefore">): string {
  const parts = [
    r.startLockHoursBefore === null
      ? "Start-of-shift blocks are not locked: they can change right up until the shift starts."
      : `The start-of-shift block is locked ${hoursText(r.startLockHoursBefore)} before each shift starts (a 9am shift locks at ${lockExample(9, r.startLockHoursBefore)}, a 3pm shift at ${lockExample(15, r.startLockHoursBefore)}). After that it is not added, resized or removed, so a start time someone checked stays put.`,
    r.endLockHoursBefore === null
      ? "End-of-shift blocks are not locked: they keep adjusting as bookings come in."
      : `The end-of-shift block is locked the same way ${hoursText(r.endLockHoursBefore)} before each shift ends (a 6pm finish locks at ${lockExample(18, r.endLockHoursBefore)}), so staff know when they finish.`,
  ];
  if (r.startLockHoursBefore !== null || r.endLockHoursBefore !== null) {
    parts.push("The one exception is a block a client has already been booked into, which still comes off until it is renamed DNB.");
    parts.push('When the time locks, the block is renamed "Auto - DNB" so reception knows it is final and should not be edited; the app never changes a DNB block after that.');
  }
  return parts.join(" ");
}

/** How a BTB block differs from a DNB block for reception; with both locks off no block becomes DNB. */
export function btbVsDnbSentence(r: Pick<RuleSet, "startLockHoursBefore" | "endLockHoursBefore">): string {
  const btb = 'A block titled "Auto - BTB" is still flexible: reception can book over it, move it or delete it by hand when a client needs that time, and the app keeps adjusting it as bookings come in.';
  if (r.startLockHoursBefore === null && r.endLockHoursBefore === null) {
    return `${btb} With schedule locks off, blocks stay BTB and never become DNB.`;
  }
  return `${btb} A block titled "Auto - DNB" is final: reception will never book over, move, or edit it, and this app never changes it.`;
}

/** The turnover rule in owner words; `maxMinutes` 0 means the rule is off. */
export function turnoverSentence(maxMinutes: number): string {
  if (maxMinutes <= 0) return "Cleaning or setup blocks get no special treatment here: a block at the very start or end of a shift keeps the BTB block off that edge.";
  return `A block of ${formatDuration(maxMinutes)} or less at the very start or end of a shift, like cleaning, a room change or setup, counts as the edge of the shift. The BTB block goes right next to it, and the open time is counted up to it.`;
}

/** Owners stop placing their own BTB blocks (spec 2026-10-13 §3): ours would treat theirs as obstacles. */
export function ownBlocksSentence(maxMinutes: number, adoptManualBtb = false): string {
  const base = adoptManualBtb
    ? "BTB blocks your team places at the start or end of a shift are managed like ours."
    : "Don't place your own BTB blocks. We place and resize them for you.";
  return maxMinutes > 0 ? `${base} Cleaning or setup blocks of ${formatDuration(maxMinutes)} or less are fine; we work around them.` : base;
}

/**
 * Each rule runs until the next one starts: `maxGap` is the next start minus one, and the last rule
 * is open-ended. Rows stay in the order given. This is the only shape the editor produces.
 */
export function chainTiers(rows: Pick<Tier, "minGap" | "blockMinutes">[]): Tier[] {
  return rows.map((t, i) => ({ minGap: t.minGap, maxGap: i < rows.length - 1 ? rows[i + 1].minGap - 1 : null, blockMinutes: t.blockMinutes }));
}

export function setTierStart(tiers: Tier[], i: number, minGap: number): Tier[] {
  return chainTiers(tiers.map((t, j) => (j === i ? { ...t, minGap } : t)));
}

export function setTierBlock(tiers: Tier[], i: number, blockMinutes: number): Tier[] {
  return tiers.map((t, j) => (j === i ? { ...t, blockMinutes } : t));
}

/** Every other rule keeps its start: removing the first leaves open time below the new first rule unblocked. */
export function removeTier(tiers: Tier[], i: number): Tier[] {
  return chainTiers(tiers.filter((_, j) => j !== i));
}

const TOP_START = RULE_STEPS.tierStartMinutes[RULE_STEPS.tierStartMinutes.length - 1];
export const canAddLargerTier = (tiers: Tier[]) => tiers.length === 0 || tiers[tiers.length - 1].minGap < TOP_START;

/** A new last rule 30 min above the current last, with the next block length up that fits. */
export function addLargerTier(tiers: Tier[]): Tier[] {
  if (tiers.length === 0) return [{ minGap: 90, maxGap: null, blockMinutes: 30 }];
  if (!canAddLargerTier(tiers)) return tiers;
  const last = tiers[tiers.length - 1];
  const minGap = last.minGap + 30;
  const blockMinutes = RULE_STEPS.blockMinutes.find((b) => b > last.blockMinutes && b <= minGap) ?? last.blockMinutes;
  return chainTiers([...tiers, { minGap, blockMinutes }]);
}

export type Choice = { value: number; label: string; disabled: boolean };

/** The fixed steps, plus a saved value off them so the page shows what is actually saved. */
function withCurrent(values: readonly number[], current: number): number[] {
  return values.includes(current) ? [...values] : [...values, current].sort((a, b) => a - b);
}

export function stepChoices(values: readonly number[], current: number, label: (v: number) => string): Choice[] {
  return withCurrent(values, current).map((value) => ({ value, label: label(value), disabled: false }));
}

/** Dropdown choices for rule i, with the ones that would break it disabled. */
export function tierChoices(tiers: Tier[], i: number): { block: Choice[]; start: Choice[] } {
  const t = tiers[i];
  const prev = i > 0 ? tiers[i - 1].minGap : null, next = i < tiers.length - 1 ? tiers[i + 1].minGap : null;
  return {
    block: withCurrent(RULE_STEPS.blockMinutes, t.blockMinutes).map((value) => ({
      value, label: formatDuration(value), disabled: value !== t.blockMinutes && value > t.minGap,
    })),
    start: withCurrent(RULE_STEPS.tierStartMinutes, t.minGap).map((value) => ({
      value, label: formatDuration(value),
      disabled: value !== t.minGap && ((prev !== null && value <= prev) || (next !== null && value >= next) || value < t.blockMinutes),
    })),
  };
}

export function tierRangeText(tiers: Tier[], i: number): string {
  const t = tiers[i], next = tiers[i + 1];
  if (!next) return `Applies to ${formatDuration(t.minGap)} open or more.`;
  return `Applies from ${formatDuration(t.minGap)} up to just under ${formatDuration(next.minGap)} open. At ${formatDuration(next.minGap)} the next rule takes over.`;
}

const WINDOW_LABELS: Record<number, string> = { 7: "1 week", 10: "10 days", 14: "2 weeks", 21: "3 weeks", 28: "4 weeks" };
export const lookaheadLabel = (days: number) => WINDOW_LABELS[days] ?? `${days} days`;

/** "the next 21 days" for one location; neutral when several locations (or none) are in view. */
export function windowPhrase(days: number | null | undefined): string {
  return days ? `the next ${days} days` : "each location's planning window";
}

export function windowSentence(days: number): string {
  return `BTB blocks are planned ${days} days ahead, today included, and kept up to date as bookings change. Shortening this takes off the blocks already placed further out.`;
}

/** Save dialog line for a window change; null when it did not change. */
export function windowChangeSentence(before: number, after: number): string | null {
  if (after > before) return `Days ${before + 1} to ${after} ahead will be planned too.`;
  if (after < before) return `BTB blocks this system placed more than ${after} days ahead will be removed.`;
  return null;
}
